import { NavLink, useLocation } from "react-router-dom";
import { Search, Hash, Flame, Target, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

const TABS = [
  { to: "/buscar", icon: Search, label: "Buscar" },
  { to: "/hashtags", icon: Hash, label: "Hashtags" },
  { to: "/", icon: Sparkles, label: "Descubrir", exact: true },
  { to: "/tops", icon: Flame, label: "Tops" },
  { to: "/nichos", icon: Target, label: "Nichos" },
];

/**
 * Premium floating glassmorphic bottom navigation.
 * Uses Framer Motion `layoutId` for a sliding active pill that morphs between tabs.
 */
export const BottomNav = () => {
  const location = useLocation();

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 px-3 pb-3 pt-2 pointer-events-none"
      style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 12px)" }}
    >
      <nav
        data-testid="bottom-nav"
        className="pointer-events-auto max-w-md mx-auto rounded-full bg-black/70 backdrop-blur-2xl border border-white/[0.08] shadow-[0_18px_60px_-20px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.05)] relative overflow-hidden"
      >
        {/* subtle inner glow */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/[0.04] to-transparent rounded-full" />

        <div className="relative grid grid-cols-5 h-[64px]">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = t.exact
              ? location.pathname === t.to
              : location.pathname === t.to || location.pathname.startsWith(t.to + "/");

            return (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.exact}
                data-testid={`bottom-tab-${t.label.toLowerCase()}`}
                className="relative flex flex-col items-center justify-center gap-0.5 group select-none"
              >
                {/* Sliding active pill */}
                {active && (
                  <motion.span
                    layoutId="active-nav-pill"
                    transition={{ type: "spring", stiffness: 380, damping: 32 }}
                    className="absolute inset-y-2 inset-x-2 rounded-full bg-gradient-to-b from-white/[0.13] to-white/[0.06] border border-white/[0.08] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)]"
                  />
                )}

                <motion.div
                  className="relative z-10"
                  animate={{ scale: active ? 1.05 : 1 }}
                  transition={{ type: "spring", stiffness: 380, damping: 24 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Icon
                    className={`w-[22px] h-[22px] transition-colors ${
                      active ? "text-rose-300" : "text-white/55 group-hover:text-white/85"
                    }`}
                    strokeWidth={active ? 2.6 : 2}
                    fill={active && t.label === "Descubrir" ? "currentColor" : "none"}
                  />
                  {active && (
                    <span className="absolute -top-1 -right-1 w-1.5 h-1.5 rounded-full bg-amber-400 shadow-[0_0_10px_rgba(245,158,11,0.8)]" />
                  )}
                </motion.div>

                <span
                  className={`relative z-10 text-[10px] tracking-[0.08em] uppercase font-semibold transition-colors ${
                    active ? "text-rose-200" : "text-white/45 group-hover:text-white/70"
                  }`}
                >
                  {t.label}
                </span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default BottomNav;
